import 'katex/dist/katex.min.css';
import TeX from '@matejmazur/react-katex';
import { useState, useEffect, useContext } from 'react';
import Graphin, { GraphinContext, Behaviors } from '@antv/graphin';
import ReactHtmlParser from 'react-html-parser';
import {
  Row,
  Col,
  Button,
  Tag,
  Table,
  Spin,
  Typography,
  Skeleton,
  Space,
  Modal,
} from 'antd';
import Chart from '../components/Chart';
import { useParams } from 'react-router';
// eslint-disable-next-line no-unused-vars
import CustomHull from '../components/CustomHull';

import useKmsApi from '../hooks/useKmsApi';
// import useKtApi from '../hooks/useKtApi'
import useProblemPredReq from '../hooks/useProblemPredReq';
import _ from 'lodash';

import {
  studentPassFailInfo,
  studentScoreInfo,
} from '../data/studentPassFailInfo';
import styles from './StudentDetail.module.css';
import studentProfiles from '../data/studentProfiles';
import learningStandard from '../data/learningStandard';
import calendarSvg from '../images/calendar.svg';
import clockSvg from '../images/clock.svg';
import Loader from '../components/Loader';
import { allKCGraphDataWithPosition } from '../data/allKCGraph';
import { Floor } from '../utils';

const { Text } = Typography;
const { DragCanvas, ZoomCanvas, DragNode } = Behaviors;
// 주소 예(kmsApi test 가능)
// fail: http://localhost:3000/47399/11-KC33/200427
// pass: http://localhost:3000/18223/11-KC22/200401
// DEMO 화면 구성 : https://docs.google.com/presentation/d/1xo_Rt7KdUQpyWp9urmtGp-ez6NuGF8qzlo9tkcRQGDk/edit
// repo : https://gitlab.com/la.iv-data/usm/date-demo/demo-front

const StudentDetail = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  // url params
  const { studentId, kcId, date } = useParams(); // TODO: date는 YYYYMMDD 형식인가 YYMMDD 형식인가?
  const [selectedKCId, setSelectedKCId] = useState(); // 최초는 선택 추천 kc가 판단되면 이를 추천 kc로 사용
  const predModelVersion = 'v20220512XLSEQ'; // 'v20210616_m2_PID_MID_STAPLE';
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setLoading(false);
      }, 4000);
    }
    // eslint-disable-next-line
  }, []);

  const studentLearningInfo = studentScoreInfo.find(
    (student) => student.studentId === Number(studentId)
  );
  const pass = studentPassFailInfo.find(
    (studentInfo) => studentInfo.studentId === parseInt(studentId, 10)
  ).pass;

  const [predList, graphData, isPredListLoading] = useKmsApi(
    kcId,
    studentId,
    date,
    pass,
    predModelVersion
  );

  /* 가져온 모든 KC 정보*/
  const kcInfoList = predList.map((item) => {
    const standard = learningStandard[item.item_id.substring(0, 2)]; // pass fail 기준값?
    const isPass = item.accuracy >= standard && item.intime >= standard;

    const foundNode = graphData.nodes.find(
      ({ properties: { _uid } }) => _uid === item.item_id
    );
    const past = studentLearningInfo?.kcInfos?.find(
      (kcInfo) => kcInfo.kcId === item.item_id
    );
    return {
      past, // 과거 학습 결과
      pred: item, // 예측
      kcId: item.item_id,
      isPass,
      title: foundNode ? foundNode.properties.title : '',
    };
  });
  const todayKC = kcInfoList.find((kc) => kc.kcId === kcId) || {}; // TODO : 이렇게 {}로 하지 말고 필요한 데이터와 표시해야하는 영역 다시 정해서 loading 혹은 Skeleton 표시하기
  // 추천 KCS는 lackKC 에서 graphData.paths 순서상 앞에 있는 KC
  const lackKCList = kcInfoList
    .filter(({ isPass }) => isPass === false)
    .filter(({ kcId }) => kcId !== todayKC.kcId);
  const goodKCList = kcInfoList
    .filter(({ isPass }) => isPass)
    .filter(({ kcId }) => kcId !== todayKC.kcId);

  let recommendedKC =
    _.minBy(lackKCList, (kc) => {
      const idx = graphData.paths.indexOf(kc.kcId);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    }) || {};
  // TODO: 이슈 : 오늘 학습한 KC pass 했는데 예측값은 fail 추천 대상이 되는 상황
  // TODO: 이슈 : 추천할게 없을 때 (lack이 없음)

  // 사용자가 직접 선택한 kc가 없을 경우 recommendedKC를 selectedKC 로 사용
  const selectedKC = selectedKCId
    ? kcInfoList.find((kc) => kc.kcId === selectedKCId) || {}
    : _.isEmpty(recommendedKC)
    ? todayKC
    : recommendedKC; // TODO : 이렇게 {}로 하지 말고 필요한 데이터와 표시해야하는 영역 다시 정해서 loading 혹은 Skeleton 표시하기
  const studentInfo = {
    status: '수업중', // 키보코에서 고정이면 미리 받고// 고정이 아니면 url param 으로 받죠.
    description: 'Ultra Speed Math', // 학생별 고정
    ...studentProfiles.find(
      (student) => student.knowreUserId === parseInt(studentId, 10)
    ),
  };

  const [problemPredState] = useProblemPredReq(
    selectedKC?.kcId,
    studentId,
    date,
    predModelVersion
  );
  const stateOfNodes = _.reduce(
    graphData.nodes,
    (prev, { properties: { title, _uid } }) => {
      prev[_uid] =
        _uid === kcId
          ? 'today'
          : _uid === recommendedKC.kcId
          ? 'recommended'
          : !studentLearningInfo?.kcInfos?.find((kc) => kc.kcId === _uid)
          ? 'yet'
          : 'normal';
      return prev;
    },
    {}
  );
  const stateOfEdges = _.reduce(
    graphData.relations,
    (prev, [source, target]) => {
      prev[source + target] = 'normal';
      return prev;
    },
    {}
  );

  const onNodeClick = (evt) => {
    const node = evt.item.getModel();
    setSelectedKCId(node.id);
  };

  const formattedDate =
    '20' +
    date.slice(0, 2) +
    '년' +
    ' ' +
    date.slice(2, 4) +
    '월' +
    ' ' +
    date.slice(4, 6) +
    '일';

  const paragraphGuidingLearning =
    '<div style={{whiteSpace: `pre-line`}}>' +
    (pass === 'pass'
      ? `오늘 학습한 <strong>'${todayKC.title}'</strong>의 학습 결과가 우수합니다.\n` +
        (_.isEmpty(goodKCList)
          ? `\n하지만, 연관 학습 단원에 대한 단계별 학습이 필요합니다. <strong>${recommendedKC.title}</strong> 학습을 추천합니다.`
          : `연관 학습 단원인 ${[...goodKCList]
              .splice(0, 3)
              .map((kc) => `<strong>'${kc.title}'</strong>`)
              .join(', ')} ${
              goodKCList.length > 3 ? '등 ' : ''
            }역시 우수한 성적이 예상됩니다.\n` +
            (_.isEmpty(recommendedKC)
              ? `\n다음 레벨 학습으로 건너뜁니다.`
              : `\n<strong>'${recommendedKC.title}'</strong> 학습으로 건너뛰어 학습을 추천합니다.`))
      : `오늘 학습한 <strong>'${todayKC.title}'</strong> 는 학습 기준에 미달되었습니다.\n` +
        (_.isEmpty(lackKCList)
          ? `선수 학습 내용에 대한 이해도는 충분한 상황으로 판단됩니다. \n\n다시, <strong>'${todayKC.title}'</strong>의 학습을 반복하여 이해도를 높이는 것을 추천합니다.`
          : `학습 부진 원인은 ${[
              recommendedKC,
              ..._.filter(lackKCList, (kc) => kc.title !== recommendedKC.title),
            ]
              .splice(0, 3)
              .map((kc) => `<strong>'${kc.title}'</strong>`)
              .join(', ')} ${
              lackKCList.length > 2 ? '등' : ''
            }에 있는것으로 판단됩니다. \n\n먼저, <strong>'${
              recommendedKC.title
            }'</strong> 의 학습을 진행하여 이해도를 높이는 것을 추천합니다.`)) +
    '</div>';

  const nodeHalo = {
    animate: {
      attrs: (ratio) => {
        const startR = 40;
        const diff = 50 - startR;
        return {
          r: startR + diff * ratio,
          opacity: 0.5 + 0.5 * ratio,
        };
      },
      duration: 200,
      easing: 'easeCubic',
      delay: 0,
      repeat: false,
      keyshape: {
        fill: '#ff0000',
        opacity: 0.8,
      },
    },
  };

  console.log('#####');
  console.log('kcInfoList', kcInfoList);
  console.log('predList', predList);
  console.log('graphData', graphData);
  console.log('todayKC', todayKC);
  console.log('selectedKCId', selectedKCId);
  console.log('selectedKC', selectedKC);
  console.log('problemPredState', problemPredState);
  return (
    <>
      {/* <Header /> */}
      <Row justify="center">
        {loading || isPredListLoading ? (
          <div className={styles.loaderWrapper}>
            <Loader />
            <p>
              학습 결과를 AI 가 분석중에 있습니다. <br /> 잠시만 기다려 주세요.{' '}
            </p>
          </div>
        ) : (
          <Col className={styles.container}>
            {/* 상단 : 프로파일과 분석 결과 */}
            <Row
              align="middle"
              justify="space-between"
              className={styles.profileContainer}
            >
              <Col span={6}>
                <StudentProfile {...studentInfo} />
              </Col>
              <Col span={18}>
                {isPredListLoading ? (
                  <Skeleton
                    active
                    size="small"
                    paragraph={{ rows: 3 }}
                    title={false}
                  />
                ) : (
                  <Row
                    align="middle"
                    justify="start"
                    className={styles.coachingRemark}
                    style={{ fontSize: '15.6px' }}
                  >
                    {ReactHtmlParser(paragraphGuidingLearning)}
                  </Row>
                )}
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <h2>AI 진단 결과</h2>
              </Col>
              <Row
                justify="space-between"
                align="middle"
                className={styles.calendarAndDetailLearningInfoBtn}
              >
                <div className={styles.calendarContainer}>
                  <img
                    className={styles.calendarIcon}
                    alt="calendar"
                    src={calendarSvg}
                  />
                  <Text className={styles.calendarText}>{formattedDate}</Text>
                </div>
                <Button
                  className={styles.detailLearningInfoBtn}
                  href={`https://rta-demo.laivdata.com/details/${studentInfo.knowreUserId}`}
                >
                  상세 학습
                </Button>
              </Row>
            </Row>

            {/* 중간 : 지식맵과 KC 그래프 */}
            <Row gutter="16" style={{ marginBottom: '20px' }}>
              <Col span={12} className={styles.knowledgeMapContainer}>
                <Row justify={'space-between'}>
                  <h3 className={styles.knowledgeMapHeader}>지식맵</h3>
                  <Button onClick={() => setIsModalVisible(true)}>
                    List view
                  </Button>
                </Row>
                <Row align="middle" style={{ marginBottom: '5px' }}>
                  <h4 className={styles.knowledgeMapTagTitle}>{'학습 추천'}</h4>
                  {isPredListLoading ? (
                    <Skeleton.Button size="small" active />
                  ) : !_.isEmpty(recommendedKC) ? (
                    <Tag
                      color="#F50"
                      style={{
                        borderRadius: 5,
                        fontSize: '12px',
                        backgroundColor: '#f50',
                      }}
                    >
                      {recommendedKC.title}
                    </Tag>
                  ) : pass !== 'pass' ? (
                    <Tag
                      color="#F50"
                      style={{
                        borderRadius: 5,
                        fontSize: '12px',
                        backgroundColor: '#f50',
                      }}
                    >
                      {todayKC.title}
                    </Tag>
                  ) : undefined}
                </Row>
                <Row align="middle" gutter={[0, 4]}>
                  <Col>
                    <h4 className={styles.knowledgeMapTagTitle}>
                      {pass === 'fail' ? '학습 부족' : '건너뛰기'}
                    </h4>
                  </Col>
                  {isPredListLoading ? (
                    <Space>
                      <Skeleton.Button size="small" active />
                      <Skeleton.Button size="small" active />
                    </Space>
                  ) : (
                    (pass === 'pass' ? goodKCList : lackKCList).map((kc) => (
                      <Tag
                        style={{
                          color: '#11263C',
                          backgroundColor: '#EDEFF3',
                          borderWidth: 0,
                        }}
                        key={kc.kcId}
                      >
                        {kc.title}
                      </Tag>
                    ))
                  )}
                </Row>
                <Row>
                  <Graphin
                    data={allKCGraphDataWithPosition}
                    state
                    layout={{ type: 'preset' }}
                    nodeStateStyles={{
                      status: {
                        hover: {},
                        active: {
                          // lineWidth: 5,
                        },
                        disabled: {},
                        inactive: {},
                        today: {
                          keyshape: {
                            size: 40,
                            opacity: 1,
                            fill: '#4a86e8',
                            fillOpacity: 0.3,
                            stroke: 'blue',
                          },
                          label: {
                            fontSize: 12,
                            fillOpacity: 1,
                          },
                          halo: nodeHalo,
                        },
                        recommended: {
                          keyshape: {
                            size: 40,
                            opacity: 1,
                            fill: '#ff0000',
                            fillOpacity: 0.3,
                            stroke: 'red',
                          },
                          label: {
                            fontSize: 12,
                            fillOpacity: 1,
                          },
                          halo: nodeHalo,
                        },
                        yet: {
                          keyshape: {
                            size: 40,
                            opacity: 1,
                            fill: '#b2b2b2',
                            fillOpacity: 0.3,
                            stroke: '#b2b2b2',
                            lineDash: [10, 5],
                          },
                          label: {
                            fontSize: 12,
                            fillOpacity: 1,
                          },
                          halo: nodeHalo,
                        },
                        normal: {
                          keyshape: {
                            size: 40,
                            opacity: 1,
                            fill: '#b2b2b2',
                            fillOpacity: 0.3,
                            stroke: '#b2b2b2',
                          },
                          label: {
                            fontSize: 12,
                            fillOpacity: 1,
                          },
                          halo: nodeHalo,
                        },
                      },
                    }}
                    edgeStateStyles={{
                      status: {
                        normal: {
                          halo: nodeHalo,
                        },
                      },
                    }}
                    defaultNode={{
                      style: {
                        keyshape: {
                          size: 40,
                          fill: '#ddd',
                          fillOpacity: 0.1,
                          stroke: '#b2b2b2',
                          strokeOpacity: 0.7,
                        },
                        label: {
                          fontSize: 8,
                          fillOpacity: 0.5,
                          offset: [0, 5],
                        },
                      },
                    }}
                    fitView={true}
                  >
                    <NodeClickBehavior onClick={onNodeClick} />
                    <ZoomCanvas />
                    <DragCanvas />
                    <DragNode />
                    <StateSetter
                      stateOfNodes={stateOfNodes}
                      stateOfEdges={stateOfEdges}
                      isLoading={isPredListLoading}
                    />
                    {/* <CustomHull
                      options={
                        [
                          // {
                          //   id: 'subg',
                          //   members: _.keys(stateOfNodes),
                          //   type: 'bubble',
                          //   padding: 50,
                          //   style: {
                          //     fill: 'lightgreen',
                          //     stroke: 'green',
                          //   },
                          // },
                        ]
                      }
                    /> */}

                    {/* <ActivateRelations /> */}
                  </Graphin>
                </Row>
              </Col>
              <Col span={12}>
                <Row
                  gutter={[0, 8]}
                  justify="start"
                  style={{ height: 'calc(100% - 8px)' }}
                >
                  <div className={styles.chartCompContainer}>
                    <Row align="middle" style={{ marginBottom: '5px' }}>
                      <h3 className={styles.chartKCHeader}>{'학습한 KC'}</h3>
                      <Tag
                        color="white"
                        style={{
                          borderRadius: 5,
                          fontSize: '12px',
                          backgroundColor: '#4a86e8',
                          borderWidth: 0,
                        }}
                      >
                        {todayKC.title}
                      </Tag>
                    </Row>
                    <Row
                      style={{
                        height: 'calc(100% - 58px)',
                      }}
                    >
                      <Col
                        span={12}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Row justify="center">
                          <p className={styles.chartKCTitle}>오늘 학습 결과</p>
                        </Row>
                        {isPredListLoading ? (
                          <Spin />
                        ) : (
                          <Chart
                            chartId="testAChart"
                            data={[
                              {
                                type: '목표시간달성률',
                                rate: Floor(todayKC?.past?.intime, 4),
                              },
                              {
                                type: '정답률',
                                rate: Floor(todayKC?.past?.accuracy, 4),
                              },
                            ]}
                          />
                        )}
                      </Col>
                      <Col
                        span={12}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Row justify="center">
                          <p className={styles.chartKCTitle}>
                            재학습 시 예측 결과
                          </p>
                        </Row>
                        {isPredListLoading ? (
                          <Spin />
                        ) : (
                          <Chart
                            chartId="testBChart"
                            data={[
                              {
                                type: '목표시간달성률',
                                rate: Floor(todayKC?.pred?.intime, 4),
                              },
                              {
                                type: '정답률',
                                rate: Floor(todayKC?.pred?.accuracy, 4),
                              },
                            ]}
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                  <div className={styles.chartCompContainer}>
                    <Row align="middle" style={{ marginBottom: '5px' }}>
                      <h3 className={styles.chartKCHeader}>{'연관학습 KC'}</h3>
                      {/* TODO: recommened KC일 때는 Tag가 빨간색이 되어야함 ㅡ*/}
                      <Tag
                        style={{
                          borderRadius: 5,
                          fontSize: '12px',
                          color:
                            selectedKC === recommendedKC ||
                            selectedKC === todayKC
                              ? 'white'
                              : '#11263C',
                          backgroundColor:
                            selectedKC === recommendedKC
                              ? '#F50'
                              : selectedKC === todayKC
                              ? '#4a86e8'
                              : '#EDEFF3',
                          borderWidth: 0,
                          // backgroundColor: 'EDEFF3',
                        }}
                      >
                        {selectedKC.title}
                      </Tag>
                    </Row>
                    <Row style={{ height: 'calc(100% - 58px)' }}>
                      <Col
                        span={12}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Row justify="center">
                          <p className={styles.chartKCTitle}>최근 학습 결과</p>
                        </Row>
                        {isPredListLoading ? (
                          <Spin />
                        ) : selectedKC.past ? (
                          <Chart
                            chartId="testCChart"
                            data={[
                              {
                                type: '목표시간달성률',
                                rate: Floor(selectedKC.past.intime, 4),
                              },
                              {
                                type: '정답률',
                                rate: Floor(selectedKC.past.accuracy, 4),
                              },
                            ]}
                          />
                        ) : (
                          <div
                            style={{
                              height: '120px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            '학습 이력 없음'
                          </div>
                        )}
                      </Col>
                      <Col
                        span={12}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Row justify="center">
                          <p className={styles.chartKCTitle}>
                            {selectedKC.past
                              ? '재학습 시 예측 결과'
                              : '학습 시 예측 결과'}
                          </p>
                        </Row>
                        {isPredListLoading ? (
                          <Spin />
                        ) : (
                          <Chart
                            chartId="testDChart"
                            data={[
                              {
                                type: '목표시간달성률',
                                rate: Floor(selectedKC?.pred?.intime, 4),
                              },
                              {
                                type: '정답률',
                                rate: Floor(selectedKC?.pred?.accuracy, 4),
                              },
                            ]}
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                </Row>
              </Col>
            </Row>

            {/* 하단 : 표 */}
            <Row>
              <Col span={24}>
                <div className={styles.tableTitle}>
                  <h3>학습 예측</h3>
                </div>
                <Table
                  className={styles.tableContent}
                  loading={problemPredState.pending}
                  dataSource={_.map(problemPredState.data, (v) => ({
                    ...v,
                    key: v.item_id,
                    kcTitle: selectedKC.title,
                    accuracy: v.accuracy,
                  }))}
                  columns={[
                    {
                      title: (
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'grey',
                            // lineHeight: '16px',
                          }}
                        >
                          문항 ID
                        </div>
                      ),
                      dataIndex: 'item_id',
                      key: 'item_id',
                      width: 100,
                      align: 'center',
                    },
                    {
                      title: (
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'grey',
                            // lineHeight: '16px',
                          }}
                        >
                          KC 명
                        </div>
                      ),
                      dataIndex: 'kcTitle',
                      key: 'kcTitle',
                      align: 'center',
                      width: 400,
                    },
                    {
                      title: (
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'grey',
                            // lineHeight: '16px',
                          }}
                        >
                          정답
                        </div>
                      ),
                      dataIndex: 'accuracy',
                      key: 'accuracy',
                      align: 'center',
                      width: 100,
                      render: (v) => {
                        return v >= 0.6 ? (
                          <CorrectAnswerMark keyword={'정답'} />
                        ) : (
                          <InCorrectAnswerMark keyword={'오답'} />
                        );
                      },
                    },
                    {
                      title: (
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'grey',
                            // lineHeight: '16px',
                          }}
                        >
                          목표시간달성
                        </div>
                      ),
                      dataIndex: 'intime',
                      key: 'intime',
                      align: 'center',
                      width: 100,
                      render: (v) => {
                        return v >= 0.6 ? 'Y' : 'N';
                      },
                    },
                    {
                      // TODO: 문항의 title을 가져와서 표시
                      title: (
                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'grey',
                            lineHeight: '16px',
                          }}
                        >
                          문항
                        </div>
                      ),
                      dataIndex: 'problemLink',
                      key: 'problemLink',
                      align: 'center',
                      render: (v) => {
                        return v.includes('frac') ? (
                          <TeX
                            math={v
                              .replaceAll('\\\\d', '')
                              .replaceAll('frac', '\\frac')}
                          />
                        ) : (
                          v
                        );
                      },
                    },
                  ]}
                  pagination={{ position: ['bottomCenter'] }}
                />
              </Col>
            </Row>
            <Modal
              title="학습 순서"
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              width={'1366px'}
              centered={true}
              footer={null}
            >
              <Table
                bordered
                scroll={{ y: 500 }}
                pagination={false}
                // dataSource={_.chain(graphData.paths)
                //   .filter((item) =>
                //     kcInfoList.find((kcInfo) => kcInfo.kcId === item)
                //   )
                //   .map((kcId, index) => {
                //     const targetKCInfo = kcInfoList.find(
                //       (kcInfo) => kcInfo.kcId === kcId
                //     );
                //     return {
                //       index: index + 1,
                //       kcName: targetKCInfo.title,
                //       isPass: targetKCInfo.isPass,
                //       isRecommended: kcId === recommendedKC.kcId,
                //     };
                //   })
                //   .value()}
                dataSource={_.chain(graphData.paths)
                  .map((kcId, index) => {
                    const targetKCInfo = (() => {
                      const targetFromKcInfo = kcInfoList.find(
                        (kcInfo) => kcInfo.kcId === kcId
                      );
                      if (targetFromKcInfo) return targetFromKcInfo;
                      const targetFromNodes = graphData.nodes.find(
                        (node) => node.uid === kcId
                      );
                      if (targetFromNodes)
                        return {
                          title: targetFromNodes.properties.title,
                          isPass: true,
                        };
                      return { title: '***', isPass: false };
                    })();
                    return {
                      index: index + 1,
                      kcId,
                      kcName: targetKCInfo.title,
                      isPass: targetKCInfo.isPass,
                      isRecommended: kcId === recommendedKC.kcId,
                    };
                  })
                  .value()}
                columns={[
                  {
                    title: '순서',
                    width: '80px',
                    dataIndex: 'index',
                    key: 'index',
                    align: 'center',
                  },
                  {
                    title: 'KC명',
                    dataIndex: 'kcName',
                    key: 'kcName',
                    align: 'center',
                    render: (value, row) => {
                      return pass !== 'pass' &&
                        _.isEmpty(recommendedKC) &&
                        row.kcId === todayKC.kcId ? (
                        <strong>{value}</strong>
                      ) : row.isRecommended ? (
                        <strong>{value}</strong>
                      ) : (
                        value
                      );
                    },
                  },
                  {
                    title: '예상 통과 여부',
                    width: '120px',
                    dataIndex: 'isPass',
                    key: 'isPass',
                    align: 'center',
                    render: (value, row) => {
                      return row.kcId === todayKC.kcId ? (
                        '학습KC'
                      ) : value ? (
                        <CorrectAnswerMark keyword={'pass'} />
                      ) : (
                        <InCorrectAnswerMark keyword={'fail'} />
                      );
                    },
                  },
                  {
                    title: '학습 추천',
                    width: '120px',
                    dataIndex: 'isRecommended',
                    key: 'isRecommended',
                    align: 'center',
                    render: (value, row) => {
                      return pass !== 'pass' &&
                        _.isEmpty(recommendedKC) &&
                        row.kcId === todayKC.kcId ? (
                        <strong>추천</strong>
                      ) : value ? (
                        <strong>추천</strong>
                      ) : null;
                    },
                  },
                ]}
              ></Table>
            </Modal>
          </Col>
        )}
      </Row>
    </>
  );
};

export default StudentDetail;

const NodeClickBehavior = ({ onClick }) => {
  const { graph } = useContext(GraphinContext);
  useEffect(() => {
    // Initialize focus to `node-1`
    // apis.focusNodeById('01');

    const events = {
      'node:click': (e) => {
        // INFO: node의 현재 위치 파악하기 위한 코드
        // const result = graph.findAll('node', () => true);
        // console.log(
        //   _.reduce(
        //     result,
        //     (prev, n) => {
        //       const m = n.getModel();
        //       console.log();
        //       prev[m.id] = {
        //         x: m.x,
        //         y: m.y,
        //       };
        //       return prev;
        //     },
        //     {}
        //   )
        // );
        // 선택할 수 없는 Node일 경우 선택 해제
        const clickNodes = graph.findAllByState('node', 'selected');
        if (
          clickNodes[0] &&
          _.difference(
            ['today', 'recommended', 'normal', 'yet'],
            clickNodes[0].getStates()
          ).length === 4
        ) {
          graph.setItemState(clickNodes[0], 'selected', false);
        } else {
          _.isFunction(onClick) && onClick(e, graph);
        }
      },
    };
    _.map(events, (fn, name) => {
      graph.on(name, fn);
    });
    return () => {
      _.map(events, (fn, name) => {
        graph.off(name, fn);
      });
    };
  }, [graph, onClick]);
  return null;
};

const StateSetter = ({ stateOfNodes, stateOfEdges, isLoading }) => {
  const { graph } = useContext(GraphinContext);
  useEffect(() => {
    if (!isLoading) {
      // 기존 states를 모두 지우고 새로운 state 지정
      graph.fitView([-300, -300]);
      const aniDuration = 2000;
      const todayId = _.findKey(stateOfNodes, (v) => v === 'today');
      if (todayId) {
        const todayNode = graph.findById(todayId);
        graph.focusItem(todayNode, true, {
          easing: 'easeCubic',
          duration: aniDuration,
        });
      }
      setTimeout(() => {
        if (stateOfNodes) {
          _.map(graph.getNodes(), (n) => {
            graph.clearItemStates(n, ['today', 'recommended', 'normal', 'yet']);
          });
          _.map(stateOfNodes, (v, k) => {
            const item = graph.findById(k);
            item && graph.setItemState(item, v, true);
          });
        }
        if (stateOfEdges) {
          _.map(graph.getEdges(), (n) => {
            graph.clearItemStates(n, ['normal']);
          });
          _.map(stateOfEdges, (v, k) => {
            const item = graph.findById(k);
            item && graph.setItemState(item, v, true);
          });
        }
      }, aniDuration);
    }
    // graph.refreshPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, isLoading]);
  return null;
};

const StudentProfile = ({ userImgUrl, status, userName, description }) => {
  return (
    <div className={styles.studentDetailWrapper}>
      <div>
        <Row align={'middle'} justify={'center'} style={{ height: '100%' }}>
          <div className={styles.studentDetail}>
            <img
              src={userImgUrl}
              alt="학생 사진"
              width="104px"
              height="104px"
            />
          </div>
        </Row>
      </div>
      <div className={styles.profileContainer}>
        <Row align="middle" className={styles.profileInfo}>
          <Col className={styles.statusWrapper}>
            <img alt="clock icon" src={clockSvg} />
            <span className={styles.status}>{status}</span>
          </Col>
          <Col span={24} className={styles.studentName}>
            {userName}
          </Col>
          <Col span={24} className={styles.studentSubject}>
            {description}
          </Col>
        </Row>
      </div>
    </div>
  );
};

// 학습 예측 표에서 정답 데이터에 따라 정오답 표기가 변경되는 컴포넌트
const CorrectAnswerMark = ({ keyword }) => {
  return (
    <div className={styles.tableAnswerCorrect}>
      <span>{keyword}</span>
    </div>
  );
};

const InCorrectAnswerMark = ({ keyword }) => {
  return (
    <div className={styles.tableAnswerInCorrect}>
      <span>{keyword}</span>
    </div>
  );
};

// const tableData = [
//   {
//     key: '1',
//     problemUID: '11-02-0005',
//     kcName: '분모가 같고 받아내림이 있는 (대분수)-(진분수),(대분수)-(대분수)',
//     isRightAnswer: '정답',
//     inTime: 'Y',
//     problemLink: '',
//   },
// ];
